import { useState } from "react";
import { ModalState, StatusModalState } from "../modalTypes";
import DistillationExperimentApi from "../../../api/Distillation/DistillationExperiment";

export type DistillationExperiment = {
  id: number;
  distillationStatusName?: string;
  [key: string]: any;
};

export type DistillationActionType = "returnToRequester" | "cancelExperiment";
export type DistillationActionResultValue = "success" | "failure";
export type DistillationActionResult = {
  action: DistillationActionType;
  result: DistillationActionResultValue;
};

interface UseDistillationExperimentActionsArgs {
  updateExperiment?: (payload: DistillationExperiment) => Promise<boolean>;
  onActionComplete: (result: DistillationActionResult) => void;
}

type ModalPreset = {
  confirm: {
    title: string;
    message: string;
  };
  status: {
    warning: StatusModalState;
    success: StatusModalState;
    fail: StatusModalState;
  };
};

const MODAL_PRESETS: Record<DistillationActionType, ModalPreset> = {
  cancelExperiment: {
    confirm: {
      title: "Confirm cancel",
      message: "Are you sure you want to cancel the selected distillation?",
    },
    status: {
      warning: {
        kind: "status",
        title: "Warning!",
        message: "You cannot cancel a cancelled experiment or a completed experiment.",
      },
      success: {
        kind: "status",
        title: "Success",
        message: "Experiment cancelled with success.",
      },
      fail: {
        kind: "status",
        title: "Fail",
        message: "Something went wrong, please try after sometime.",
      },
    },
  },
  returnToRequester: {
    confirm: {
      title: "Confirm return",
      message: "Are you sure you want to return the selected distillation to the requester?",
    },
    status: {
      warning: {
        kind: "status",
        title: "Warning!",
        message: "Experiment cannot be returned to requester in its current state.",
      },
      success: {
        kind: "status",
        title: "Success",
        message: "Experiment returned to requester successfully.",
      },
      fail: {
        kind: "status",
        title: "Fail",
        message: "Something went wrong, please try after sometime.",
      },
    },
  },
};

export function useDistillationExperimentActions({
  updateExperiment,
  onActionComplete,
}: UseDistillationExperimentActionsArgs) {
  const [activeModal, setActiveModal] = useState<ModalState | null>(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [resultOnStatusClose, setResultOnStatusClose] = useState<DistillationActionResult | null>(
    null,
  );

  const closeModal = () => {
    const result = activeModal?.kind === "status" ? resultOnStatusClose : null;

    setActiveModal(null);
    setResultOnStatusClose(null);

    if (result) {
      onActionComplete(result);
    }
  };

  const canReturnToRequester = (experiment: DistillationExperiment) =>
    experiment.distillationStatusName === "Submitted";

  const canCancelExperiment = (experiment: DistillationExperiment) =>
    experiment.distillationStatusName !== "Completed" &&
    experiment.distillationStatusName !== "Cancelled";

  const updateDistillationExperiment = async (payload: DistillationExperiment) => {
    if (updateExperiment) {
      return updateExperiment(payload);
    }

    const response = await DistillationExperimentApi.updateExperiment(payload);
    return !!(response && response.message === "Success");
  };

  const startReturnToRequester = (experiment: DistillationExperiment) => {
    setActiveModal({
      kind: "confirm",
      title: MODAL_PRESETS.returnToRequester.confirm.title,
      message: MODAL_PRESETS.returnToRequester.confirm.message,
      onCancel: closeModal,
      onConfirm: async () => {
        closeModal();

        if (!canReturnToRequester(experiment)) {
          setActiveModal(MODAL_PRESETS.returnToRequester.status.warning);
          setResultOnStatusClose(null);
          return;
        }

        const payload = {
          ...experiment,
          distillationStatusName: "Needs Review",
        };

        setIsProcessingAction(true);
        try {
          const result = await updateDistillationExperiment(payload);
          if (result) {
            setActiveModal(MODAL_PRESETS.returnToRequester.status.success);
            setResultOnStatusClose({ action: "returnToRequester", result: "success" });
          } else {
            setActiveModal(MODAL_PRESETS.returnToRequester.status.fail);
            setResultOnStatusClose({ action: "returnToRequester", result: "failure" });
          }
        } finally {
          setIsProcessingAction(false);
        }
      },
    });
  };

  const startCancelExperiment = (experiment: DistillationExperiment) => {
    setActiveModal({
      kind: "confirm",
      title: MODAL_PRESETS.cancelExperiment.confirm.title,
      message: MODAL_PRESETS.cancelExperiment.confirm.message,
      onCancel: closeModal,
      onConfirm: async () => {
        closeModal();

        if (!canCancelExperiment(experiment)) {
          setActiveModal(MODAL_PRESETS.cancelExperiment.status.warning);
          setResultOnStatusClose(null);
          return;
        }

        const payload = {
          ...experiment,
          distillationStatusName: "Cancelled",
        };

        setIsProcessingAction(true);
        try {
          const result = await updateDistillationExperiment(payload);
          if (result) {
            setActiveModal(MODAL_PRESETS.cancelExperiment.status.success);
            setResultOnStatusClose({ action: "cancelExperiment", result: "success" });
          } else {
            setActiveModal(MODAL_PRESETS.cancelExperiment.status.fail);
            setResultOnStatusClose({ action: "cancelExperiment", result: "failure" });
          }
        } finally {
          setIsProcessingAction(false);
        }
      },
    });
  };

  return {
    isProcessingAction,
    activeModal,
    closeModal,
    canReturnToRequester,
    canCancelExperiment,
    startReturnToRequester,
    startCancelExperiment,
  };
}
